import { Checkbox } from "@src/components/ui-components/Checkbox/Checkbox";
import { SearchInput } from "@src/components/ui-components/Inputs/SearchInput/SearchInput";
import { t } from "i18next";
import { useEffect, useState } from "react";
import { UserNameLabel } from "../../../components/deprecated/Text/Label/UserNameLabel";
import { Text } from "../../../components/deprecated/Text/Text";
import { UserImageView } from "../../../components/deprecated/UserImageView/UserImageView";
import { ContractorGroupData } from "../../../to-be-removed/LocalData/ContractorGroup/ContractorGroupData";
import { ContractorGroupMemberData } from "../../../to-be-removed/LocalData/ContractorGroup/ContractorGroupMemberData";
import { CollectionLoadedListener } from "../../../to-be-removed/LocalData/General/CollectionLoader";
import { MemberData } from "../../../to-be-removed/LocalData/Property/MemberData";
import { PropertyData } from "../../../to-be-removed/LocalData/Property/PropertyData";
import { UnitData } from "../../../to-be-removed/LocalData/Property/Units/UnitData";
import { Role } from "../../../to-be-removed/LocalData/User/Role";
import styles from "./DeviationMembers.module.css";

interface DeviationMembersProps {
  property?: PropertyData;
  contractorGroups?: ContractorGroupData[];
  units?: UnitData[];

  className?: string;
  selectedMembers?: MemberData[];
  excludeCurrentUser?: boolean;
  setSelectedMembers: (members: MemberData[]) => void;
}

export const DeviationMembers = (props: DeviationMembersProps) => {
  const [, setLastUpdate] = useState(new Date());
  const [selectedMembers, setSelectedMembers] = useState<MemberData[]>(props.selectedMembers ?? []);
  const [searchText, setSearchText] = useState("");

  const property = props.property;
  const units = props.units ?? [];
  const contractorGroups = props.contractorGroups ?? [];

  const allMembers: MemberData[] = property?.members.documents ?? [];
  let filteredMembers = searchText ? allMembers.filter((member) => member.user.name.toLowerCase().includes(searchText.toLowerCase())) : allMembers;
  filteredMembers = filteredMembers.sort((a, b) => a.user.name.localeCompare(b.user.name));

  const landlords: MemberData[] = filteredMembers.filter((member) => member.roles.includes(Role.LANDLORD));
  const tenants: MemberData[] = filteredMembers.filter((member) => !landlords.includes(member) && member.units.some((unit) => units.includes(unit)));
  const contractors: MemberData[] = filteredMembers.filter((member) => member.contractorGroups.some((c) => contractorGroups.includes(c)));
  const operationManagers: MemberData[] = filteredMembers.filter(
    (member) => member.isOperationManager && !landlords.includes(member) && !tenants.includes(member) && !contractors.includes(member)
  );

  useEffect(() => {
    let listener: CollectionLoadedListener<MemberData> | undefined = undefined;
    if (property) {
      listener = property.members.setupRealtimeListener(() => {
        setLastUpdate(new Date());
      });
    }
    return () => {
      listener?.removeListener();
    };
  }, [property]);

  function updateSelectedMembers(member: MemberData, selected: boolean) {
    let newSelectedMembers = [...selectedMembers];
    if (!selectedMembers.includes(member) && selected) {
      newSelectedMembers.push(member);
    } else if (selectedMembers.includes(member) && !selected) {
      newSelectedMembers = newSelectedMembers.filter((selectedMember) => selectedMember !== member);
    }
    setSelectedMembers(newSelectedMembers);
    props.setSelectedMembers(newSelectedMembers);
  }

  return (
    <div className={styles.responsiblePopUp + " " + props.className}>
      <SearchInput value={searchText} onChange={setSearchText} marginTop={16} placeholder={t("main.searchForUser")} />
      {operationManagers.length > 0 && (
        <MembersSection
          key={"operationManager"}
          title={t("main.operationManager")}
          members={operationManagers}
          selectedMembers={selectedMembers}
          updateSelectedMembers={(member, checked) => updateSelectedMembers(member, checked)}
        />
      )}
      <MembersSection
        key={"landlord"}
        title={t("main.landlords")}
        members={landlords}
        selectedMembers={selectedMembers}
        updateSelectedMembers={(member, checked) => updateSelectedMembers(member, checked)}
      />
      {contractorGroups.map((contractorGroup) => (
        <MembersSection
          key={contractorGroup.id}
          title={contractorGroup.name}
          members={contractors.filter((c) => c.contractorGroups.includes(contractorGroup))}
          selectedMembers={selectedMembers}
          updateSelectedMembers={(member, checked) => updateSelectedMembers(member, checked)}
        />
      ))}
      {units.map((tenantUnit: UnitData) => (
        <MembersSection
          key={tenantUnit.id}
          title={tenantUnit.name}
          members={tenants.filter((tenant) => tenant.units.includes(tenantUnit))}
          selectedMembers={selectedMembers}
          updateSelectedMembers={(member, checked) => updateSelectedMembers(member, checked)}
        />
      ))}
      {selectedMembers.length > 0 && (
        <MembersSection
          key={"selected"}
          title={t("main.selectedUsers")}
          members={selectedMembers}
          selectedMembers={selectedMembers}
          updateSelectedMembers={(member, checked) => updateSelectedMembers(member, checked)}
        />
      )}
    </div>
  );
};
interface MembersSectionProps {
  selectedMembers: MemberData[];
  members: MemberData[];
  title: string;
  key: string;
  updateSelectedMembers: (member: MemberData, checked: boolean) => void;
}
const MembersSection = (props: MembersSectionProps) => {
  return (
    <>
      <Text size="md" isBold text={props.title} className={styles.subheader} />
      <table key={props.key} className={styles.usersTable}>
        <tbody>
          {props.members.map((member) => (
            <UserCell
              checked={props.selectedMembers.includes(member)}
              key={member.id}
              member={member}
              updateChecked={(checked) => props.updateSelectedMembers(member, checked)}
            />
          ))}
        </tbody>
      </table>
    </>
  );
};

interface UserCellProps {
  member: MemberData | ContractorGroupMemberData;
  updateChecked: (checked: boolean) => void;
  checked: boolean;
}

const UserCell = (props: UserCellProps) => {
  return (
    <tr className={styles.userBox}>
      <td>
        <UserImageView user={props.member.user} className={styles.responsibleImage} size={28} />
      </td>
      <td className={styles.nameTd} onClick={() => props.updateChecked(!props.checked)}>
        <UserNameLabel user={props.member.user} type="paragraph" className={styles.responsibleName} disableUserPopUp />
      </td>
      <td className={styles.checkboxTd}>
        <Checkbox value={props.checked} className={styles.checkbox} onChange={(checked: boolean) => props.updateChecked(checked)} />
      </td>
    </tr>
  );
};
